import React, { useState, useCallback } from 'react';
import { Plus, Trash2, DollarSign, Percent, Receipt, ShieldCheck, CheckCircle, XCircle, AlertCircle, Stamp, Eye, EyeOff } from 'lucide-react';
import type { DealVariable, DealVariableFormData } from '../../lib/types';
import { useDealVariableStore } from '../../lib/stores/dealVariableStore';

interface DealVariablesSectionProps {
  variables: DealVariable[];
  onVariablesChange: (variables: DealVariable[]) => void;
  dealServiceId?: string; // For database operations when editing existing services
  readonly?: boolean;
}

export function DealVariablesSection({
  variables,
  onVariablesChange,
  dealServiceId,
  readonly = false
}: DealVariablesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { createServiceVariable, updateServiceVariable, deleteServiceVariable, confirmVariable, unconfirmVariable } = useDealVariableStore();
  
  // Debounced update function
  const debouncedUpdate = useCallback(
    (() => {
      const timeouts = new Map<string, NodeJS.Timeout>();
      
      return (variableId: string, updates: Partial<DealVariable>) => {
        // Clear existing timeout for this variable
        if (timeouts.has(variableId)) {
          clearTimeout(timeouts.get(variableId)!);
        }
        
        // Set new timeout
        const timeout = setTimeout(async () => {
          if (dealServiceId && variableId && !variableId.startsWith('temp-')) {
            try {
              await updateServiceVariable(variableId, updates);
            } catch (error) {
              console.error('Error updating service variable:', error);
            }
          }
          timeouts.delete(variableId);
        }, 1000); // Wait 1 second after user stops typing
        
        timeouts.set(variableId, timeout);
      };
    })(),
    [dealServiceId, updateServiceVariable]
  );

  const addVariable = async () => {
    if (dealServiceId) {
      // For existing services, create variable in database
      const newVariableData: DealVariableFormData = {
        name: '',
        description: '',
        amount: 0,
        type: 'fixed',
        is_cost: false,
        is_internal_cost: false,  // Default: costo esterno fatturabile
        is_vat_exempt: false,
        is_stamp_duty: false,
        is_stamp_duty_art15: false,
        include_in_pdf: true,
        sort_order: variables.length
      };

      try {
        const createdVariable = await createServiceVariable(dealServiceId, newVariableData);
        if (createdVariable) {
          onVariablesChange([...variables, createdVariable]);
        }
      } catch (error) {
        console.error('Error creating service variable:', error);
      }
    } else {
      // For new services, create temporary variable
      const newVariable: DealVariable = {
        id: `temp-${Date.now()}`,
        organization_id: '',
        deal_service_id: '',
        name: '',
        description: '',
        amount: 0,
        type: 'fixed',
        base_amount: undefined,
        is_cost: false,
        is_internal_cost: false,
        is_vat_exempt: false,
        is_stamp_duty: false,
        is_stamp_duty_art15: false,
        include_in_pdf: true,
        is_confirmed: false,
        sort_order: variables.length,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      onVariablesChange([...variables, newVariable]);
    }
  };

  const updateVariable = (index: number, updates: Partial<DealVariable>) => {
    const variable = variables[index];
    
    // Always update local state immediately for responsive UI
    const updatedVariables = variables.map((v, i) => 
      i === index ? { ...v, ...updates } : v
    );
    onVariablesChange(updatedVariables);
    
    // For existing services with saved variables, debounce database update
    if (dealServiceId && variable.id && !variable.id.startsWith('temp-')) {
      debouncedUpdate(variable.id, updates);
    }
  };

  const removeVariable = async (index: number) => {
    const variable = variables[index];

    if (dealServiceId && variable.id && !variable.id.startsWith('temp-')) {
      // For existing services with saved variables, delete from database
      await deleteServiceVariable(variable.id);
    }

    // Remove from local state
    const updatedVariables = variables.filter((_, i) => i !== index);
    onVariablesChange(updatedVariables);
  };

  const handleConfirmVariable = async (variable: DealVariable, index: number) => {
    if (!dealServiceId || !variable.id || variable.id.startsWith('temp-')) {
      return;
    }

    const confirmed = await confirmVariable(variable.id);
    if (confirmed) {
      const updatedVariables = [...variables];
      updatedVariables[index] = confirmed;
      onVariablesChange(updatedVariables);
    }
  };

  const handleUnconfirmVariable = async (variable: DealVariable, index: number) => {
    if (!dealServiceId || !variable.id || variable.id.startsWith('temp-')) {
      return;
    }

    const unconfirmed = await unconfirmVariable(variable.id);
    if (unconfirmed) {
      const updatedVariables = [...variables];
      updatedVariables[index] = unconfirmed;
      onVariablesChange(updatedVariables);
    }
  };

  const formatAmount = (amount: number, type: 'fixed' | 'percentage') => {
    if (type === 'percentage') {
      return `${amount}%`;
    }
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="space-y-3">
      {/* Header Notion-like: label uppercase Flusia color + count discreto +
          bottone "+ Aggiungi" testuale, no card heavy. */}
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-[#E91E8C]">
            Variabili e costi
          </span>
          {variables.length > 0 && (
            <span className="text-[11px] text-black/45 dark:text-white/45">
              {variables.length}
            </span>
          )}
        </div>

        {!readonly && (
          <button
            type="button"
            onClick={addVariable}
            className="inline-flex items-center gap-1 text-[12px] text-black/55 dark:text-white/55 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] px-1.5 py-0.5 rounded transition-colors"
          >
            <Plus className="h-3 w-3" />
            <span>Aggiungi</span>
          </button>
        )}
      </div>

      {/* Variables List */}
      {variables.length > 0 && (
        <div className="space-y-2">
          {variables.map((variable, index) => (
            <div key={variable.id} className={`rounded-md border bg-white dark:bg-white/[0.03] p-3 ${variable.is_confirmed ? 'border-emerald-200 dark:border-emerald-800/40' : 'border-black/[0.08] dark:border-white/[0.08]'}`}>
              {/* Status Badge */}
{/*               <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {variable.is_confirmed ? (
                    <span className="inline-flex items-center space-x-1 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      <CheckCircle className="h-3 w-3" />
                      <span>Confermata</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center space-x-1 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                      <AlertCircle className="h-3 w-3" />
                      <span>Bozza</span>
                    </span>
                  )}
                </div>

                {!readonly && dealServiceId && !variable.id.startsWith('temp-') && (
                  <button
                    type="button"
                    onClick={() => variable.is_confirmed ? handleUnconfirmVariable(variable, index) : handleConfirmVariable(variable, index)}
                    className={`inline-flex items-center space-x-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                      variable.is_confirmed
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {variable.is_confirmed ? (
                      <>
                        <XCircle className="h-4 w-4" />
                        <span>Sconferma</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        <span>Conferma</span>
                      </>
                    )}
                  </button>
                )}
              </div> */}

              {/* Notion-style: input inline senza box pieno, label uppercase
                  tracking-wide piccola, border-bottom solo on focus. La griglia
                  passa a 2 colonne su md (Nome/Descrizione/Tipo/Importo a sinistra,
                  flag a destra) per leggere meglio. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* Campi principali */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide font-medium text-black/55 dark:text-white/55 mb-0.5">
                      Nome <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={variable.name}
                      onChange={(e) => updateVariable(index, { name: e.target.value })}
                      placeholder="Es. Trasferta, Sconto…"
                      className="w-full px-1 py-1 text-[13px] bg-transparent border-0 border-b border-transparent hover:border-black/[0.1] dark:hover:border-white/15 focus:border-black/30 dark:focus:border-white/40 text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30 outline-none focus:ring-0 transition-colors"
                      disabled={readonly}
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wide font-medium text-black/55 dark:text-white/55 mb-0.5">
                      Descrizione
                    </label>
                    <input
                      type="text"
                      value={variable.description || ''}
                      onChange={(e) => updateVariable(index, { description: e.target.value })}
                      placeholder="Dettaglio opzionale"
                      className="w-full px-1 py-1 text-[13px] bg-transparent border-0 border-b border-transparent hover:border-black/[0.1] dark:hover:border-white/15 focus:border-black/30 dark:focus:border-white/40 text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30 outline-none focus:ring-0 transition-colors"
                      disabled={readonly}
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wide font-medium text-black/55 dark:text-white/55 mb-0.5">
                      Tipo
                    </label>
                    <select
                      value={variable.type}
                      onChange={(e) => updateVariable(index, { type: e.target.value as 'fixed' | 'percentage' })}
                      className="w-full px-1 py-1 text-[13px] bg-transparent border-0 border-b border-transparent hover:border-black/[0.1] dark:hover:border-white/15 focus:border-black/30 dark:focus:border-white/40 text-black dark:text-white outline-none focus:ring-0 transition-colors cursor-pointer"
                      disabled={readonly || variable.is_stamp_duty}
                    >
                      <option value="fixed">Fisso</option>
                      <option value="percentage">Percentuale</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wide font-medium text-black/55 dark:text-white/55 mb-0.5">
                      Importo <span className="text-red-500">*</span>
                    </label>
                    <div className="relative flex items-center gap-1">
                      <span className="text-[12px] text-black/45 dark:text-white/45 flex-shrink-0">
                        {variable.type === 'percentage' ? '%' : '€'}
                      </span>
                      <input
                        type="number"
                        value={variable.amount}
                        onChange={(e) => updateVariable(index, { amount: parseFloat(e.target.value) || 0 })}
                        step="0.01"
                        min="0"
                        max={variable.type === 'percentage' ? '100' : undefined}
                        placeholder="0"
                        className="w-full px-1 py-1 text-[13px] bg-transparent border-0 border-b border-transparent hover:border-black/[0.1] dark:hover:border-white/15 focus:border-black/30 dark:focus:border-white/40 text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30 outline-none focus:ring-0 transition-colors"
                        disabled={readonly}
                      />
                    </div>
                  </div>
                </div>

                {/* Flag — stack verticale compatto, Notion-like */}
                <div className="space-y-1">
                  <label className="flex items-center gap-2 cursor-pointer text-[12px] text-black/75 dark:text-white/75 py-0.5 px-1 -mx-1 rounded hover:bg-black/[0.03] dark:hover:bg-white/[0.05]">
                    <input
                      type="checkbox"
                      checked={variable.is_internal_cost ?? false}
                      onChange={(e) => updateVariable(index, {
                        is_internal_cost: e.target.checked,
                        is_cost: e.target.checked
                      })}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      disabled={readonly}
                    />
                    <Receipt className="h-3.5 w-3.5 text-black/55 dark:text-white/55 flex-shrink-0" />
                    <span>Costo interno <span className="text-black/40 dark:text-white/40">(non fatturabile)</span></span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-[12px] text-black/75 dark:text-white/75 py-0.5 px-1 -mx-1 rounded hover:bg-black/[0.03] dark:hover:bg-white/[0.05]">
                    <input
                      type="checkbox"
                      checked={variable.is_vat_exempt}
                      onChange={(e) => updateVariable(index, { is_vat_exempt: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      disabled={readonly || variable.is_stamp_duty}
                    />
                    <ShieldCheck className="h-3.5 w-3.5 text-black/55 dark:text-white/55 flex-shrink-0" />
                    <span>Esente IVA</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-[12px] text-black/75 dark:text-white/75 py-0.5 px-1 -mx-1 rounded hover:bg-black/[0.03] dark:hover:bg-white/[0.05]">
                    <input
                      type="checkbox"
                      checked={variable.include_in_pdf ?? true}
                      onChange={(e) => updateVariable(index, { include_in_pdf: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      disabled={readonly}
                    />
                    {(variable.include_in_pdf ?? true) ? (
                      <Eye className="h-3.5 w-3.5 text-black/55 dark:text-white/55 flex-shrink-0" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5 text-black/55 dark:text-white/55 flex-shrink-0" />
                    )}
                    <span>Mostra nel documento <span className="text-black/40 dark:text-white/40">(mandato/preventivo)</span></span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-[12px] text-black/75 dark:text-white/75 py-0.5 px-1 -mx-1 rounded hover:bg-black/[0.03] dark:hover:bg-white/[0.05]">
                    <input
                      type="checkbox"
                      checked={variable.is_stamp_duty}
                      onChange={(e) => {
                        const isStampDuty = e.target.checked;
                        updateVariable(index, {
                          is_stamp_duty: isStampDuty,
                          ...(isStampDuty && { is_vat_exempt: true, type: 'fixed' as const })
                        });
                      }}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      disabled={readonly}
                    />
                    <Stamp className="h-3.5 w-3.5 text-black/55 dark:text-white/55 flex-shrink-0" />
                    <span>Marca da bollo</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-[12px] text-black/75 dark:text-white/75 py-0.5 px-1 -mx-1 rounded hover:bg-black/[0.03] dark:hover:bg-white/[0.05]">
                    <input
                      type="checkbox"
                      checked={variable.is_stamp_duty_art15}
                      onChange={(e) => {
                        const isStampDutyArt15 = e.target.checked;
                        updateVariable(index, {
                          is_stamp_duty_art15: isStampDutyArt15,
                          ...(isStampDutyArt15 && {
                            is_vat_exempt: true,
                            is_internal_cost: false,
                            is_cost: false,
                            type: 'fixed' as const
                          })
                        });
                      }}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
                      disabled={readonly}
                    />
                    <Stamp className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
                    <span>Marca da bollo Art. 15</span>
                  </label>

                  {!readonly && (
                    <button
                      type="button"
                      onClick={() => removeVariable(index)}
                      className="mt-1 inline-flex items-center gap-1 text-[12px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 px-1.5 py-0.5 rounded transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span>Rimuovi</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Anteprima Calcolo — riga compatta sotto i campi, no border-top heavy */}
              {variable.amount > 0 && (
                <div className="mt-2 pt-2 border-t border-black/[0.05] dark:border-white/[0.06] flex items-center justify-between text-[12px]">
                  <span className="text-black/60 dark:text-white/55">
                    {variable.is_cost ? 'Costo' : 'Variabile'} · {formatAmount(variable.amount, variable.type)}
                  </span>
                  <div className="flex items-center gap-1">
                    {variable.is_cost && (
                      <span className="text-[10px] uppercase tracking-wide font-medium text-red-700 dark:text-red-400">Costo</span>
                    )}
                    {variable.is_cost && variable.is_vat_exempt && <span className="text-black/30 dark:text-white/30">·</span>}
                    {variable.is_vat_exempt && (
                      <span className="text-[10px] uppercase tracking-wide font-medium text-amber-700 dark:text-amber-400">Esente IVA</span>
                    )}
                    {(variable.is_cost || variable.is_vat_exempt) && variable.is_stamp_duty && <span className="text-black/30 dark:text-white/30">·</span>}
                    {variable.is_stamp_duty && (
                      <span className="text-[10px] uppercase tracking-wide font-medium text-purple-700 dark:text-purple-400">Bollo</span>
                    )}
                    {(variable.is_cost || variable.is_vat_exempt || variable.is_stamp_duty) && variable.include_in_pdf === false && <span className="text-black/30 dark:text-white/30">·</span>}
                    {variable.include_in_pdf === false && (
                      <span className="text-[10px] uppercase tracking-wide font-medium text-black/50 dark:text-white/50">Nascosta dal documento</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Riepilogo — Notion-like: nessuna card pesante, riga di stat
          compatte separate da bullets, hint discreto sotto. */}
      {variables.length > 0 && (
        <div className="pt-3 border-t border-black/[0.06] dark:border-white/[0.08]">
          <div className="text-[10px] uppercase tracking-wide font-medium text-black/45 dark:text-white/45 mb-1.5">
            Riepilogo
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
            <span>
              <span className="text-black/55 dark:text-white/55">Bozze</span>{' '}
              <span className="font-medium text-amber-600 dark:text-amber-400">
                {variables.filter(v => !v.is_confirmed).length}
              </span>
            </span>
            <span>
              <span className="text-black/55 dark:text-white/55">Confermate</span>{' '}
              <span className="font-medium text-emerald-600 dark:text-emerald-400">
                {variables.filter(v => v.is_confirmed).length}
              </span>
            </span>
            <span>
              <span className="text-black/55 dark:text-white/55">Variabili</span>{' '}
              <span className="font-medium text-black dark:text-white">
                {variables.filter(v => !v.is_cost).length}
              </span>
            </span>
            <span>
              <span className="text-black/55 dark:text-white/55">Costi</span>{' '}
              <span className="font-medium text-black dark:text-white">
                {variables.filter(v => v.is_cost).length}
              </span>
            </span>
          </div>
          <div className="mt-2">
            <p className="text-[11px] text-black/45 dark:text-white/45">
              Solo le variabili <span className="font-medium text-black/65 dark:text-white/65">confermate</span> vengono incluse nel calcolo delle provvigioni.
            </p>
          </div>
        </div>
      )}

      {/* Empty state Notion-like: discreto, no icone grandi */}
      {variables.length === 0 && (
        <p className="text-[12px] text-black/45 dark:text-white/45">
          Nessuna variabile. Clicca <span className="font-medium">Aggiungi</span> per
          inserirne una.
        </p>
      )}
    </div>
  );
}